import React, { createContext, useContext, useState } from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";

const OrderContext = createContext();

/* ── Status Maps ─────────────────────────────────────────── */

const STATUS_COL_MAP = {
  // Forward / production
  "Placed":                   "placed",
  "placed":                   "placed",
  "Processing":               "processing",
  "processing":               "processing",
  "Printing Started":         "printing_started",
  "printing_started":         "printing_started",
  "Production Completed":     "production_completed",
  "production_completed":     "production_completed",
  "Quality Check":            "quality_check",
  "quality_check":            "quality_check",
  "Packaging":                "packaging",
  "packaging":                "packaging",
  "Shipped":                  "shipped",
  "shipped":                  "shipped",
  "Delivered":                "delivered",
  "delivered":                "delivered",
  "Completed":                "completed",
  "completed":                "completed",

  // Cancellation
  "Cancellation Requested":   "cancellation_requested",
  "cancellation_requested":   "cancellation_requested",
  "cancel_requested":         "cancellation_requested",  // ← both map to same col
  "Cancelled":                "cancelled-notrefunded",
  "Cancelled-Refunded":       "cancelled-refunded",
  "cancelled-refunded":       "cancelled-refunded",
  "Cancelled-NotRefunded":    "cancelled-notrefunded",
  "cancelled-notrefunded":    "cancelled-notrefunded",
  "Cancelled (Not Refunded)": "cancelled-notrefunded",
  "Rejected":                 "rejected",
  "rejected":                 "rejected",
  "Cancelled-Cancelled":      "Cancelled-Cancelled",

  // Returns
  "Return Requested":         "return_requested",
  "return_requested":         "return_requested",
  "Return Approved":          "return_approved",
  "return_approved":          "return_approved",
  "Return Rejected":          "return_rejected",
  "return_rejected":          "return_rejected",
  "Returned":                 "returned",
  "returned":                 "returned",
  "return_received":          "returned",
  "Returned (No Refund)":     "returned_no_refund",
  "returned_no_refund":       "returned_no_refund",

  // Inspection
  "Inspection Pending":       "inspection_pending",
  "inspection_pending":       "inspection_pending",
  "Inspection Passed":        "inspection_passed",
  "inspection_passed":        "inspection_passed",
  "Inspection Failed":        "inspection_failed",
  "inspection_failed":        "inspection_failed",

  // Refunds & replacements
  "Refund":                   "cancelled-refunded",
  "Refund Processing":        "refund_processing",
  "refund_processing":        "refund_processing",
  "Refunded":                 "refunded",
  "refunded":                 "refunded",
  "Partial Refund":           "partial_refund",
  "partial_refund":           "partial_refund",
  "Replacement":              "Replacement",
  "Replacement Processing":   "replacement_processing",
  "replacement_processing":   "replacement_processing",
};

const COL_BACKEND_MAP = {
  "placed":                "Placed",
  "processing":            "Processing",
  "packaging":             "Packaging",
  "shipped":               "Shipped",
  "returned":              "Returned",
  "delivered":              "Delivered",
  "cancelled-refunded":    "Cancelled-Refunded",
  "Cancelled-Cancelled":   "Cancelled-Cancelled",
  "cancelled-notrefunded": "Cancelled-NotRefunded",
  "rejected":              "Rejected",
  "Replacement":           "Replacement",
};

/* ── Helpers ─────────────────────────────────────────────── */

const prefixCdn = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${CDN_URL}/${url}`;
};

const parseReturnMedia = (rd) => {
  const imageArr = rd.images || rd.evidence_images || rd.image_urls || [];
  const images = Array.isArray(imageArr) && imageArr.length > 0
    ? imageArr.map(prefixCdn).filter(Boolean)
    : rd.image ? [prefixCdn(rd.image)] : [];

  const videoArr = rd.videos || rd.evidence_videos || rd.video_urls || rd.return_videos || [];
  const videos = Array.isArray(videoArr) && videoArr.length > 0
    ? videoArr.map(prefixCdn).filter(Boolean)
    : rd.video ? [prefixCdn(rd.video)] : [];

  return { images, videos };
};

// Pulls a return reason (id or raw label) out of a status-history row's
// return_details blob, trying every key shape the backend has used.
const extractReturnReason = (h) => {
  const rd = h?.return_details;
  if (!rd || typeof rd !== "object") return null;
  const value = rd.reason ?? rd.return_reason ?? rd.returnReason ?? null;
  return value !== null && value !== undefined && value !== "" ? value : null;
};

/* ── Order Mapper ────────────────────────────────────────── */

const mapOrder = (o) => {
  const firstName = o.user?.first_name || "";
  const lastName  = o.user?.last_name  || "";

  const shippedEntry = [...(o.status_histories || [])]
    .reverse()
    .find(
      (h) =>
        h.status === "Shipped" &&
        (
          h.shipping_details ||
          h.vendor ||
          h.tracking_id ||
          h.pickup_date
        )
    );
  const returnEntry = (o.status_histories || []).find(
    (h) => h.return_details && Object.keys(h.return_details).length > 0
  );

  const rd        = returnEntry?.return_details || {};

  const rawStatus = o.order_status || "Placed";
  const colId     = STATUS_COL_MAP[rawStatus] || "placed";

  const { images: returnImages, videos: returnVideos } = parseReturnMedia(rd);

  const parseCategory = (description) => {
    try {
      const d = JSON.parse(description || "[]");
      return Array.isArray(d) && d[0]?.title ? d[0].title : null;
    } catch {
      return null;
    }
  };

  return {
    id:          o.id,
    orderNumber: o.order_id || `#${o.id}`,

    status:    colId,
    rawStatus: rawStatus,

    date: o.placed_audit_time,

    payment_method:  o.payment_method  || "COD",
    payment_status:  o.payment_status  || "Pending",
    paymentMethod:   o.payment_method  || "COD",
    transaction_id:  o.transaction_id  || null,
    payment_gateway: o.payment_gateway || null,

    subtotal:         parseFloat(o.subtotal         || 0),
    shipping:         parseFloat(o.shipping_charges || 0),
    shipping_charges: parseFloat(o.shipping_charges || 0),
    tax:              parseFloat(o.tax              || 0),
    total:            parseFloat(o.total            || 0),

    orderType:      o.order_type ?? null,
    orderTypeLabel: o.order_type === 1 ? "Standard"
                  : o.order_type === 2 ? "Bulk"
                  : o.order_type === 3 ? "Custom"
                  : o.order_type != null ? ` ${o.order_type}`
                  : "Onsite",
    customisations: o.customisations ?? null,
    isCustom: o.customisations === "custom",

    customer: {
      id:     o.user?.id,
      name:   o.customer_name || `${firstName} ${lastName}`.trim() || "Unknown",
      email:  o.customer_email || o.user?.email || "",
      phone:  o.user?.phone || o.address?.phone || "",
      gender: o.user?.gender || "",
      avatar: o.user?.image ? `${CDN_URL}/${o.user.image}` : null,
    },

    shippingAddress: o.address ? {
      name:    o.address.address,
      street:  o.address.street,
      city:    o.address.city,
      state:   o.address.state,
      zipCode: o.address.zipcode,
      phone:   o.address.phone,
    } : null,

    billingAddress: o.billing_address ? {
      name:    [o.billing_address.first_name, o.billing_address.last_name].filter(Boolean).join(" ") || null,
      email:   o.billing_address.email   || null,
      phone:   o.billing_address.phone   || null,
      street:  o.billing_address.address || null,
      city:    o.billing_address.city    || null,
      state:   o.billing_address.state   || null,
      zipCode: o.billing_address.pincode || null,
    } : null,

    items: (o.items || []).map((i) => {
      let parsedImages = [];
      try { parsedImages = JSON.parse(i.product?.product_images || "[]"); } catch {}

      return {
        id:           i.id,
        product_id:   i.product_id,
        variation_id: i.variation_id || i.variant_id || null,
        name:         i.product?.product_name || `Product #${i.product_id}`,
        category:     parseCategory(i.product?.description),
        price:        parseFloat(i.price    || 0),
        quantity:     i.quantity,
        total:        parseFloat(i.total    || 0),
        image:        parsedImages[0] ? `${CDN_URL}/${parsedImages[0]}` : null,
      };
    }),

    // NOTE: `returnReason` is now populated per-entry (extracted from that
    // row's return_details) so the modal's timeline renderer — which reads
    // event.returnReason directly — shows the reason both before AND after
    // a refresh, instead of only in the locally-appended (pre-refresh) copy.
    timeline: (o.status_histories || []).map((h) => ({
      status:       h.status,
      date:         h.create_audit_time,
      description:  h.content || null,
      returnReason: extractReturnReason(h),

      shippingDetails:
        h.shipping_details ||
        (
          h.vendor || h.tracking_id || h.pickup_date
            ? {
                vendor: h.vendor || "",
                tracking_id: h.tracking_id || "",
                pickup_date: h.pickup_date || "",
              }
            : null
        ),

      returnDetails: h.return_details || null,
    })),

    status_histories: (o.status_histories || []).map((h) => ({
      id:         h.id,
      status:     h.status,
      content:    h.content,
      created_at: h.create_audit_time,

      shipping_details:
        h.shipping_details ||
        (
          h.vendor || h.tracking_id || h.pickup_date
            ? {
                vendor: h.vendor || "",
                tracking_id: h.tracking_id || "",
                pickup_date: h.pickup_date || "",
              }
            : null
        ),

      return_details: h.return_details || null,
    })),

    vendor:
      shippedEntry?.shipping_details?.vendor ||
      shippedEntry?.vendor ||
      "",

    trackingNumber:
      shippedEntry?.shipping_details?.tracking_id ||
      shippedEntry?.tracking_id ||
      "",

    trackingId:
      shippedEntry?.shipping_details?.tracking_id ||
      shippedEntry?.tracking_id ||
      "",

    pickupTime:
      shippedEntry?.shipping_details?.pickup_date ||
      shippedEntry?.pickup_date ||
      "",

    // Backend stores/returns the return reason as an id (e.g. "1").
    // Keep it under returnReasonId so the UI can resolve it to a label,
    // while returnReason stays available for any legacy text values.
    returnReasonId:    rd.reason ?? rd.return_reason ?? null,
    returnReason:       rd.reason           || null,
    returnDescription: rd.user_description || null,
    returnImage:       rd.image            || null,
    returnImages,
    returnVideos,

    returnDays:    7,
    returnFlags:   0,
    customerFlags: 0,
    deliveredOn:   null,
    returnPolicy:  null,
    rejectedBy:    null,
  };
};

/* ── Provider ────────────────────────────────────────────── */

export const OrderProvider = ({ children }) => {
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const getToken   = () => localStorage.getItem("token");
  const getAdminId = () => localStorage.getItem("admin_id");

  const authHeaders = () => ({
    Authorization: `Bearer ${getToken()}`,
  });

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const res  = await fetch(`${BASE_URL}/orders`, { headers: authHeaders() });
      const json = await res.json();
      const raw  = json.data || json.orders || [];
      setOrders(Array.isArray(raw) ? raw.map(mapOrder) : []);
    } catch {
      setError("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderById = async (orderId) => {
    try {
      setLoading(true);
      const res  = await fetch(`${BASE_URL}/orders/${orderId}`, { headers: authHeaders() });
      const json = await res.json();
      const raw  = json.data || json.order || json;
      if (raw?.id) {
        const mapped = mapOrder(raw);
        setOrders((prev) => prev.map((o) => (o.id === mapped.id ? mapped : o)));
        return mapped;
      }
    } catch {
      setError("Failed to load order details");
    } finally {
      setLoading(false);
    }
    return null;
  };

  const uploadReturnMedia = async (files) => {
    if (!files || files.length === 0) return [];
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    try {
      const res  = await fetch(`${BASE_URL}/upload`, {
        method:  "POST",
        headers: authHeaders(), // no Content-Type — browser sets multipart boundary
        body:    formData,
      });
      const json = await res.json();
      console.log("UPLOAD RESPONSE:", json);
      // adjust to match your API's response shape:
      return (json.urls || json.data || []).map(prefixCdn).filter(Boolean);
    } catch (err) {
      console.error("UPLOAD FAILED:", err);
      return [];
    }
  };

  const updateOrderStatus = async (orderId, newStatus, extra = {}, newDisplayStatus) => {

    console.log("newstatus", newStatus);

    try {

      setLoading(true);

      let backendStatus = COL_BACKEND_MAP[newStatus] || newStatus;

      if (
        ["cancelled-notrefunded", "Cancelled-NotRefunded", "Cancelled-Cancelled"].includes(newStatus)
      ) {
        backendStatus = "Cancelled";
      }

      if (
        ["cancelled-refunded", "Cancelled-Refunded", "Refund"].includes(newStatus)
      ) {
        backendStatus = "Refund";
      }

      const colId = STATUS_COL_MAP[backendStatus] || "placed";

      // ── Shipping-edit detection ──────────────────────────────────
      // A "shipping edit" is a PATCH to an order that is ALREADY Shipped,
      // where we're only updating carrier/tracking/pickup info — NOT a
      // fresh transition into Shipped (e.g. from Packaging or Replacement
      // Processing). The caller (OrderDetailsModal) tells us definitively
      // via extra.isShippingEdit; we also fall back to checking the
      // order's current in-memory status in case an older caller doesn't
      // pass that flag. Relying only on "did extra carry vendor/tracking/
      // pickup" (the old check) was wrong because the FIRST shipment also
      // always carries those fields — that bug is what caused "Shipped"
      // to be silently dropped from the timeline until a full refresh.
      const existingOrder = orders.find((o) => o.id === orderId);
      const wasAlreadyShipped =
        String(existingOrder?.rawStatus || existingOrder?.status || "").toLowerCase() === "shipped";

      const isShippingEdit =
        (backendStatus === "Shipped" || backendStatus === "shipped") &&
        (extra.isShippingEdit === true || wasAlreadyShipped) &&
        (extra.vendor || extra.trackingNumber || extra.pickupTime);

      // ✅ Skip optimistic update for shipping edit
      if (!isShippingEdit) {

        setOrders((prev) =>
          prev.map((o) =>
            o.id === orderId
              ? {
                  ...o,
                  status: colId,
                  rawStatus: backendStatus
                }
              : o
          )
        );

      }

      const payload = {
        order_status: backendStatus,
        audit_id: getAdminId(),
        content:
          extra.cancelReason ||
          extra.description ||
          null,

        ...(extra.vendor !== undefined &&
          extra.vendor !== null && {
            vendor: extra.vendor,
          }),

        ...(extra.trackingNumber !== undefined &&
          extra.trackingNumber !== null && {
            tracking_id: extra.trackingNumber,
          }),

        ...(extra.pickupTime !== undefined &&
          extra.pickupTime !== null && {
            pickup_date: extra.pickupTime,
          }),

        // ── returnReason / returnNote ────────────────────────────────
        // Verified in Postman: this endpoint expects CAMELCASE keys here
        // ("returnReason", "returnNote") — unlike order_status / audit_id
        // which are snake_case. returnReason's value is the reason's id,
        // sent as a string (e.g. "1"). `returnReasonId` is the canonical
        // source, set by the reason picker in ReturnPolicyModal.
        ...(extra.returnReasonId !== undefined && extra.returnReasonId !== null && {
          returnReason: String(extra.returnReasonId),
        }),

        // Fallback for any older callers passing a raw value directly
        // instead of going through returnReasonId.
        ...(extra.returnReasonId === undefined && extra.returnReason && {
          returnReason: String(extra.returnReason),
        }),

        ...(extra.returnNote !== undefined && extra.returnNote !== null && extra.returnNote !== "" && {
          returnNote: extra.returnNote,
        }),

        ...(extra.rejection_reason && {
          rejection_reason: extra.rejection_reason,
        }),

        ...(extra.rejectionReason && {
          rejection_reason: extra.rejectionReason,
        }),

        ...(extra.returnImages?.length && {
          return_images: extra.returnImages,
        }),

        ...(extra.returnVideos?.length && {
          return_videos: extra.returnVideos,
        }),

        ...(extra.flag_count !== undefined && {
          flag_count: extra.flag_count,
        }),

        ...(extra.account_blocked !== undefined && {
          account_blocked: extra.account_blocked,
        }),

        ...(extra.refundStatus && {
          refund_status: extra.refundStatus,
        }),
      };
      console.log("payload :", payload)
      const res = await fetch(
        `${BASE_URL}/orders/${orderId}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders()
          },
          body: JSON.stringify(payload),
        }
      );

      const json = await res.json();

      if (!res.ok) {

        setError(json.message || "Failed to update status");

        await fetchOrders();

        return false;
      }

      console.log("data", json.data);

      if (json.data) {

        // ✅ SHIPPING DETAILS UPDATE ONLY
        if (isShippingEdit) {

          setOrders((prev) =>
            prev.map((o) => {

              if (o.id !== orderId) return o;

              // ✅ find latest shipped index
              const shippedIndex = [...(o.timeline || [])]
                .map((t, i) => ({
                  index: i,
                  status: String(t.status).toLowerCase()
                }))
                .reverse()
                .find((x) => x.status === "shipped")?.index;

              // ✅ update only latest shipped row
              const updatedTimeline = (o.timeline || []).map((t, i) => {

                if (i !== shippedIndex) return t;

                return {
                  ...t,
                  shippingDetails: {
                    vendor: extra.vendor || "",
                    tracking_id: extra.trackingNumber || "",
                    pickup_date: extra.pickupTime || "",
                  }
                };
              });

              // ✅ remove duplicate shipped rows
              const uniqueTimeline = updatedTimeline.filter((item, index, arr) => {

                if (String(item.status).toLowerCase() !== "shipped") {
                  return true;
                }

                return index === shippedIndex;
              });

              return {
                ...o,

                vendor: extra.vendor || "",
                trackingNumber: extra.trackingNumber || "",
                trackingId: extra.trackingNumber || "",
                pickupTime: extra.pickupTime || "",

                timeline: uniqueTimeline,

                status_histories: (o.status_histories || []).map((h, i, arr) => {

                  const latestShippedIndex = [...arr]
                    .map((x, idx) => ({
                      index: idx,
                      status: String(x.status).toLowerCase()
                    }))
                    .reverse()
                    .find((x) => x.status === "shipped")?.index;

                  if (i !== latestShippedIndex) return h;

                  return {
                    ...h,
                    shipping_details: {
                      vendor: extra.vendor || "",
                      tracking_id: extra.trackingNumber || "",
                      pickup_date: extra.pickupTime || "",
                    }
                  };
                })
              };
            })
          );
        } else {

          // ✅ NORMAL STATUS UPDATE
          const updated = mapOrder(json.data);

          setOrders((prev) =>
            prev.map((o) =>
              o.id === updated.id
                ? updated
                : o
            )
          );
        }
      }

      return true;

    } catch (err) {

      console.error(err);

      setError("Failed to update order status");

      return false;

    } finally {

      setLoading(false);

    }
  };

  const updateOrder = async (orderId, patch) => {
    setOrders((prev) =>
      prev.map((o) => {
        if (o.id !== orderId) return o;
        const next = { ...o, ...patch };
        if (patch.status) next.rawStatus = COL_BACKEND_MAP[patch.status] || patch.status;
        return next;
      })
    );
    if (patch.status) await updateOrderStatus(orderId, patch.status);
  };

  const createOrder = async (payload) => {
    try {
      setLoading(true);
      const res  = await fetch(`${BASE_URL}/orders`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body:    JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.message || "Failed to create order");
        return false;
      }
      const newOrder = mapOrder(json.data);
      setOrders((prev) => [newOrder, ...prev]);
      return newOrder;
    } catch {
      setError("Failed to create order");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const createBulkOrder = async (payload) => {
    try {
      setLoading(true);
      setError(null);
      const res  = await fetch(`${BASE_URL}/bulk-order`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body:    JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.message || "Failed to create bulk order");
        return false;
      }
      await fetchOrders();
      return json.data || true;
    } catch {
      setError("Failed to create bulk order");
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <OrderContext.Provider value={{
      orders, loading, error,
      fetchOrders, fetchOrderById,
      updateOrderStatus, updateOrder, createOrder, createBulkOrder,
      uploadReturnMedia,
      STATUS_COL_MAP, COL_BACKEND_MAP,
    }}>
      {children}
    </OrderContext.Provider>
  );
};

export const useOrders = () => useContext(OrderContext);
export default OrderContext;